<script setup lang="ts">
import { type Bid, BidType, type ProductDetails } from "mws-ts-v2-sdk";

type Props = {
  sortedBids: Bid[];
  product: ProductDetails;
  obfuscatedUserId?: number;
};

const { sortedBids, product } = defineProps<Props>();

const { productTabViewed } = useAnalytics();
const onShowMore = () => {
  productTabViewed({
    tab_name: "Bids",
    event_name: product.eventName ?? undefined,
    product_name: product.name,
  });
};

const hasAutomaticBids = computed(() =>
  sortedBids.some((bid) => bid.bidType === BidType.AutomaticBid),
);
</script>

<template>
  <ProductDescriptionDrawerBase
    v-if="sortedBids.length > 0"
    :is-collapsable="sortedBids.length > 3"
    :title="
      product.finished ? $t('product.bidHistory') : $t('product.latestBids')
    "
    @show-more="onShowMore"
  >
    <template
      v-if="sortedBids.length"
      #content="{ isCollapsed }"
    >
      <table class="flex flex-col gap-1">
        <ProductBidListHeader />
        <tbody>
          <ProductBidListItem
            v-for="(bid, index) in isCollapsed
              ? sortedBids
              : sortedBids.slice(0, 3)"
            :key="bid.bidId"
            :bid="bid"
            :bidder-is-user="obfuscatedUserId === bid.obfuscatedUserId"
            data-test="historical-bid"
            :data-cy="`bid-list-item-nth-${index + 1}`"
          />
        </tbody>
        <ProductBidListFooter v-if="hasAutomaticBids" />
      </table>
    </template>
    <template #showMore>
      {{
        $t("product.seeAllBids", {
          PLACEHOLDER_numberOfBids: sortedBids.length,
        })
      }}
    </template>
  </ProductDescriptionDrawerBase>
</template>
